import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { API } from "@/App";
import { usePermissions } from "@/hooks/usePermissions";
import axios from "axios";
import { toast } from "sonner";
import {
  Users,
  Plus,
  MagnifyingGlass,
  CaretRight,
  Phone,
  EnvelopeSimple,
  DownloadSimple
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const STATUS_LABELS = {
  nouveau: "Nouveau",
  contacte: "Contacté",
  en_discussion: "En discussion",
  offre_envoyee: "Offre envoyée",
  reserve: "Réservé",
  converti: "Converti",
  perdu: "Perdu"
};

const TYPE_LABELS = {
  particulier: "Particulier",
  professionnel: "Professionnel",
  prospect: "Prospect"
};

const INITIAL_CLIENT = {
  nom: "", prenom: "", telephone: "", email: "",
  adresse: "", type_client: "prospect", notes_commerciales: "",
  numero_registre_national: "", numero_tva: ""
};

const ClientRow = ({ client }) => (
  <tr key={client.client_id} data-testid={`client-row-${client.client_id}`}>
    <td>
      <p className="font-medium text-slate-900">{client.prenom} {client.nom}</p>
    </td>
    <td>
      <div className="flex flex-col gap-0.5">
        {client.telephone && (
          <span className="flex items-center gap-1 text-slate-600 text-xs">
            <Phone size={12} /> {client.telephone}
          </span>
        )}
        {client.email && (
          <span className="flex items-center gap-1 text-slate-600 text-xs">
            <EnvelopeSimple size={12} /> {client.email}
          </span>
        )}
      </div>
    </td>
    <td>
      <span className="text-xs text-slate-500">{TYPE_LABELS[client.type_client] || client.type_client}</span>
    </td>
    <td>
      <span className={`badge badge-${client.statut_commercial}`}>
        {STATUS_LABELS[client.statut_commercial] || client.statut_commercial}
      </span>
    </td>
    <td className="text-right">
      <Link to={`/clients/${client.client_id}`} data-testid={`view-client-${client.client_id}`}
        className="inline-flex items-center text-blue-600 hover:text-blue-700 font-medium text-sm">
        Voir <CaretRight size={14} className="ml-0.5" />
      </Link>
    </td>
  </tr>
);

const Clients = () => {
  const { can } = usePermissions();
  const [clients, setClients] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newClient, setNewClient] = useState(INITIAL_CLIENT);

  const fetchClients = useCallback(async () => {
    try {
      const params = {};
      if (searchTerm) params.search = searchTerm;
      const response = await axios.get(`${API}/clients`, { params });
      setClients(response.data.data || []);
      setTotal(response.data.total || 0);
    } catch (error) {
      console.error("Error fetching clients:", error);
      toast.error("Erreur lors du chargement des clients");
    } finally {
      setLoading(false);
    }
  }, [searchTerm]);

  useEffect(() => {
    fetchClients();
  }, [fetchClients]);

  const handleCreateClient = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/clients`, newClient);
      toast.success("Client créé avec succès");
      setDialogOpen(false);
      setNewClient(INITIAL_CLIENT);
      fetchClients();
    } catch (error) {
      console.error("Error creating client:", error);
      toast.error("Erreur lors de la création du client");
    }
  };

  const canCreateClient = can("clients", "create");

  const handleExportCSV = async () => {
    try {
      const response = await axios.get(`${API}/export/clients?format=csv`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'clients_export.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Export CSV téléchargé");
    } catch {
      toast.error("Erreur lors de l'export");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner h-10 w-10"></div>
      </div>
    );
  }

  return (
    <div data-testid="clients-page" className="animate-fade-in">
      <div className="page-header">
        <div>
          <h1 className="page-title">Clients</h1>
          <p className="page-subtitle">{total} client{total !== 1 ? "s" : ""}</p>
        </div>
        <div className="page-actions">
          <Button variant="outline" onClick={handleExportCSV} data-testid="export-clients-csv">
            <DownloadSimple size={18} className="mr-2" /> Export CSV
          </Button>
          {canCreateClient && (
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button data-testid="add-client-btn" className="btn-primary">
                  <Plus size={18} className="mr-2" /> Ajouter un client
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg">
                <DialogHeader>
                  <DialogTitle className="text-heading-3">Nouveau client</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleCreateClient} className="space-y-4 mt-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="form-label">Prénom *</Label>
                      <Input data-testid="input-prenom" value={newClient.prenom} onChange={(e) => setNewClient({ ...newClient, prenom: e.target.value })} required />
                    </div>
                    <div>
                      <Label className="form-label">Nom *</Label>
                      <Input data-testid="input-nom" value={newClient.nom} onChange={(e) => setNewClient({ ...newClient, nom: e.target.value })} required />
                    </div>
                  </div>
                  <div>
                    <Label className="form-label">Téléphone</Label>
                    <Input data-testid="input-telephone" value={newClient.telephone} onChange={(e) => setNewClient({ ...newClient, telephone: e.target.value })} />
                  </div>
                  <div>
                    <Label className="form-label">Email</Label>
                    <Input data-testid="input-email" type="email" value={newClient.email} onChange={(e) => setNewClient({ ...newClient, email: e.target.value })} />
                  </div>
                  <div>
                    <Label className="form-label">Type</Label>
                    <Select value={newClient.type_client} onValueChange={(val) => setNewClient({ ...newClient, type_client: val })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {Object.entries(TYPE_LABELS).map(([k, v]) => (<SelectItem key={k} value={k}>{v}</SelectItem>))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="form-label">Adresse</Label>
                    <Textarea data-testid="input-adresse" value={newClient.adresse} onChange={(e) => setNewClient({ ...newClient, adresse: e.target.value })} rows={2} />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="form-label">N° Registre National</Label>
                      <Input data-testid="input-rrn" value={newClient.numero_registre_national} onChange={(e) => setNewClient({ ...newClient, numero_registre_national: e.target.value })} placeholder="YY.MM.DD-XXX.XX" />
                    </div>
                    <div>
                      <Label className="form-label">N° TVA</Label>
                      <Input data-testid="input-tva" value={newClient.numero_tva} onChange={(e) => setNewClient({ ...newClient, numero_tva: e.target.value })} placeholder="BE 0XXX.XXX.XXX" />
                    </div>
                  </div>
                  <div>
                    <Label className="form-label">Notes commerciales</Label>
                    <Textarea data-testid="input-notes" value={newClient.notes_commerciales} onChange={(e) => setNewClient({ ...newClient, notes_commerciales: e.target.value })} rows={3} />
                  </div>
                  <div className="flex justify-end gap-3 pt-4">
                    <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Annuler</Button>
                    <Button type="submit" data-testid="submit-client-btn" className="btn-primary">Créer le client</Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>

      {/* Search */}
      <div className="relative mb-6">
        <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input data-testid="search-clients" placeholder="Rechercher par nom, email, téléphone..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10" />
      </div>

      {/* Client List */}
      {clients.length === 0 ? (
        <div className="empty-state">
          <Users size={56} className="empty-state-icon" />
          <p className="empty-state-title">Aucun client trouvé</p>
          <p className="empty-state-description">{searchTerm ? "Modifiez vos critères de recherche" : "Commencez par ajouter un client"}</p>
        </div>
      ) : (
        <div className="card overflow-hidden">
          <table className="data-table">
            <thead>
              <tr>
                <th>Client</th>
                <th>Contact</th>
                <th>Type</th>
                <th>Statut</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {clients.map((client) => (
                <ClientRow key={client.client_id} client={client} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default Clients;
